import { useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { hasCustomerToken } from '../../auth'
import { match, Route, Switch } from 'react-router'
import { useIntl, defineMessages } from 'react-intl'
import { fetchClientData } from '../../actions/client'
import { AppLayout } from '../../App/Layout/AppLayout'
import { useDispatch, useSelector } from 'react-redux'
import { setCurrentClient } from '../../actions/client'
import { fetchClientHomes } from '../../actions/client'
import { setCurrentHomeDetail } from '../../actions/home'
import { NAVIGATION_TAB_NAMES } from '../../types/navigation'
import { selectHomeFetchId } from '../../store/selectors/home'
import { getHomeIdFromPath } from '../../constants/navigation'
import { fetchMortgageRates } from '../../actions/mortgageRates'
import { getClientIdFromCalculatorsPath } from '../../constants/navigation'
import { setActiveNavigationTab } from '../../store/slices/navigation'
import topographicalMapDark from '../../assets/homes/img/topo-bg-wide.svg'
import topographicalMapLight from '../../assets/homes/img/topo-bg-light.png'
import mortgageCalculatorPBH from './assets/mortgage-calculator-pbh.svg'
import mortgageCalculatorDark from './assets/mortgage-calculator-dark.svg'
import mortgageCalculatorLight from './assets/mortgage-calculator-light.svg'
import affordabilityCalculatorPBH from './assets/affordability-calculator-pbh.svg'
import affordabilityCalculatorDark from './assets/affordability-calculator-dark.svg'
import affordabilityCalculatorLight from './assets/affordability-calculator-light.svg'
import shortTermRentalCalculatorPBH from './assets/short-term-rental-calculator-pbh.svg'
import shortTermRentalCalculatorDark from './assets/short-term-rental-calculator-dark.svg'
import shortTermRentalCalculatorLight from './assets/short-term-rental-calculator-light.svg'
import { useCreateClientEventOnView } from '../../hooks/useCreateClientEventOnView'
import { ClientEventName, ClientEventSource } from '../../api/gqlaxy/gql/generated/graphql'
import { Box, Card, Flex, Text, Heading, useTheme, CardBody } from '@homebotapp/hb-react-component-catalog'
import { EstimatedMonthlyPaymentBreakdownCustomerDM } from '../../Components/homeSearch/ListingDetails/EstimatedMonthlyPayment/EstimatedMonthlyPaymentBreakdown/EstimatedMonthlyPaymentBreakdownCustomerDM/EstimatedMonthlyPaymentBreakdownCustomerDM'
import { ShortTermRentalCalculator } from './ShortTermRentalCalculator/ShortTermRentalCalculator'
import { ActiveOverlayContextProvider } from '../../Components/shared/Routing/OverlayContext'
import { AffordabilityCalculator } from './AffordabilityCalculator/AffordabilityCalculator'
import { MortgageCalculator } from './MortgageCalculator/MortgageCalculator'
import { selectClientId } from '../../store/selectors/client'

export const MSG = defineMessages({
  calculators: {
    id: 'Calculators.calculators',
    defaultMessage: 'Financial Calculators'
  },
  understandTheMath: {
    id: 'Calculators.understandTheMath',
    defaultMessage: 'Understand the math behind your home finances'
  },
  mortgageCalculator: {
    id: 'Calculators.mortgageCalculator',
    defaultMessage: 'Monthly Mortgage Payment'
  },
  mortgageCalculatorDescription: {
    id: 'Calculators.mortgageCalculatorDescription',
    defaultMessage: 'See how much that perfect home would cost each month'
  },
  affordabilityCalculator: {
    id: 'Calculators.affordabilityCalculator',
    defaultMessage: 'Home Affordability'
  },
  affordabilityCalculatorDescription: {
    id: 'Calculators.affordabilityCalculatorDescription',
    defaultMessage: 'Understand how much you can afford based on your finances'
  },
  shortTermRentalCalculator: {
    id: 'Calculators.shortTermRentalCalculator',
    defaultMessage: 'Short-Term Rental'
  },
  shortTermRentalCalculatorDescription: {
    id: 'Calculators.shortTermRentalCalculatorDescription',
    defaultMessage: 'Use real-time data to see what  renting out a home could earn'
  }
})

export interface CalculatorsProps {
  match: match<{ buyerId?: string }>
}

export const Calculators = ({ match }: CalculatorsProps) => {
  const intl = useIntl()
  const history = useHistory()
  const { theme } = useTheme()
  const dispatch = useDispatch()
  const matchedHomeId = getHomeIdFromPath()
  const clientId = useSelector(selectClientId)
  const matchedClientId = getClientIdFromCalculatorsPath()
  const homeFetchId = useSelector(selectHomeFetchId)
  const createViewedClientEvent = useCreateClientEventOnView({
    eventName: ClientEventName.View,
    eventSource: ClientEventSource.Calculators
  })

  createViewedClientEvent()

  useEffect(() => {
    if (matchedClientId && (matchedClientId !== match.params.buyerId || matchedClientId !== clientId)) {
      dispatch(setCurrentClient(matchedClientId))
    }

    if (matchedHomeId && matchedHomeId !== homeFetchId) {
      dispatch(setCurrentHomeDetail(matchedHomeId))
      // allows impersonator to have access to a client homeId after refreshing homesearch or opening it new tab
      if (hasCustomerToken()) {
        localStorage.setItem('impersonatedClientHomeId', matchedHomeId)
      }
    }
  }, [match.params.buyerId, dispatch, homeFetchId, matchedClientId, matchedHomeId])

  useEffect(() => {
    dispatch(fetchMortgageRates())
    dispatch(setActiveNavigationTab(NAVIGATION_TAB_NAMES.CALCULATORS))

    // Dispatch client side effects
    if (match.params.buyerId) {
      dispatch(fetchClientData(match.params.buyerId))
      dispatch(fetchClientHomes(match.params.buyerId))

      // allows impersonator to have access to a client ID so that "own" nav link works after refreshing homesearch
      if (hasCustomerToken()) {
        localStorage.setItem('impersonatedClientId', match.params.buyerId)
      }
    }
  }, [match.params.buyerId, dispatch])

  return (
    <>
      {/* Nested these routes like this to avoid having to make all of the useEffect calls above in each of the below components */}
      <ActiveOverlayContextProvider>
        <Switch>
          <Route
            path={`/calculators/:buyerId([-0-9a-f]{36})?/mortgage-calculator`}
            render={props => <MortgageCalculator match={props.match} />}
          />
          <Route
            path={`/calculators/:buyerId([-0-9a-f]{36})?/affordability-calculator`}
            render={props => <AffordabilityCalculator match={props.match} />}
          />
          <Route
            path={`/calculators/:buyerId([-0-9a-f]{36})?/short-term-rental-calculator`}
            render={props => <ShortTermRentalCalculator match={props.match} />}
          />
        </Switch>
      </ActiveOverlayContextProvider>
      <AppLayout loading={false} enableAgentDropdown match={match}>
        <Box
          pt={12}
          mt={-2}
          pb={12}
          px={[5, null, 20]}
          backgroundSize='cover'
          borderBottom='2px solid'
          borderColor='neutral.200'
          backgroundBlendMode='multiply'
          backgroundColor='whiteAlpha.50'
          _dark={{
            backgroundColor: 'blackAlpha.600'
          }}
          backgroundImage={`url(${theme.isLightThemeType ? topographicalMapLight : topographicalMapDark})`}
        >
          <Flex maxWidth='1600px' mx='auto' pb={4} alignItems='center' justifyContent='space-between'>
            <Box>
              <Heading as='h1' size='2xl' textAlign='left' fontWeight={800} m={0} mb={4}>
                {intl.formatMessage(MSG.calculators)}
              </Heading>
              <Text m={0} size='sm' color='neutral.900'>
                {intl.formatMessage(MSG.understandTheMath)}
              </Text>
            </Box>
          </Flex>
        </Box>
        <Box mb={12} px={[5, null, 20]} py={[5, null, 10]}>
          <Flex
            gap={8}
            mx='auto'
            align='center'
            maxWidth='1600px'
            direction={['column', null, null, null, null, 'row']}
          >
            <Card
              size='lg'
              width='100%'
              cursor='pointer'
              maxWidth='500px'
              onClick={() => {
                history.push(`/calculators/${match.params.buyerId}/mortgage-calculator`)
              }}
              flex={['1 1 100%', null, null, null, null, '1 1 33%']}
              _hover={{
                border: '1px solid',
                transform: 'scale(1.025)',
                borderColor: 'neutral.400',
                transition: 'all 0.2s ease-in-out',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              <CardBody p={0}>
                <Box px={4} borderBottom='2px solid' borderColor='neutral.200'>
                  <Box
                    margin='auto'
                    height='12rem'
                    width='18.75rem'
                    backgroundSize='cover'
                    backgroundPosition='center'
                    backgroundImage={`url(${
                      theme.isLightNeutral
                        ? mortgageCalculatorPBH
                        : theme.isLightThemeType
                        ? mortgageCalculatorLight
                        : mortgageCalculatorDark
                    })`}
                  />
                </Box>
                <Box p={4}>
                  <Heading as='h3' size='md' textAlign='left' mb={2}>
                    {intl.formatMessage(MSG.mortgageCalculator)}
                  </Heading>
                  <Text m={0} size='sm'>
                    {intl.formatMessage(MSG.mortgageCalculatorDescription)}
                  </Text>
                </Box>
              </CardBody>
            </Card>
            <Card
              size='lg'
              width='100%'
              cursor='pointer'
              maxWidth='500px'
              flex={['1 1 100%', null, null, null, null, '1 1 33%']}
              onClick={() => {
                history.push(`/calculators/${match.params.buyerId}/affordability-calculator`)
              }}
              _hover={{
                border: '1px solid',
                transform: 'scale(1.025)',
                borderColor: 'neutral.400',
                transition: 'all 0.2s ease-in-out',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              <CardBody p={0}>
                <Box px={4} borderBottom='2px solid' borderColor='neutral.200'>
                  <Box
                    margin='auto'
                    height='12rem'
                    width='18.75rem'
                    backgroundSize='cover'
                    backgroundPosition='center'
                    backgroundImage={`url(${
                      theme.isLightNeutral
                        ? affordabilityCalculatorPBH
                        : theme.isLightThemeType
                        ? affordabilityCalculatorLight
                        : affordabilityCalculatorDark
                    })`}
                  />
                </Box>
                <Box p={4}>
                  <Heading as='h3' size='md' textAlign='left' mb={2}>
                    {intl.formatMessage(MSG.affordabilityCalculator)}
                  </Heading>
                  <Text m={0} size='sm'>
                    {intl.formatMessage(MSG.affordabilityCalculatorDescription)}
                  </Text>
                </Box>
              </CardBody>
            </Card>
            <Card
              size='lg'
              width='100%'
              cursor='pointer'
              maxWidth='500px'
              flex={['1 1 100%', null, null, null, null, '1 1 33%']}
              onClick={() => {
                history.push(`/calculators/${match.params.buyerId}/short-term-rental-calculator`)
              }}
              _hover={{
                border: '1px solid',
                transform: 'scale(1.025)',
                borderColor: 'neutral.400',
                transition: 'all 0.2s ease-in-out',
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
              }}
            >
              <CardBody p={0}>
                <Box px={4} borderBottom='2px solid' borderColor='neutral.200'>
                  <Box
                    margin='auto'
                    height='12rem'
                    width='18.75rem'
                    backgroundSize='cover'
                    backgroundPosition='center'
                    backgroundImage={`url(${
                      theme.isLightNeutral
                        ? shortTermRentalCalculatorPBH
                        : theme.isLightThemeType
                        ? shortTermRentalCalculatorLight
                        : shortTermRentalCalculatorDark
                    })`}
                  />
                </Box>
                <Box p={4}>
                  <Heading as='h3' size='md' textAlign='left' mb={2}>
                    {intl.formatMessage(MSG.shortTermRentalCalculator)}
                  </Heading>
                  <Text m={0} size='sm'>
                    {intl.formatMessage(MSG.shortTermRentalCalculatorDescription)}
                  </Text>
                </Box>
              </CardBody>
            </Card>
          </Flex>
          <EstimatedMonthlyPaymentBreakdownCustomerDM buyerId={match.params.buyerId} />
        </Box>
      </AppLayout>
    </>
  )
}
